/**
 * AssetDationForm — o BEM dado em pagamento (dação de bens).
 *
 * ⚠️ Era uma LISTA, com "Adicionar bem" e remoção por value. Agora é UM bem por pagamento: quem
 * precisa registrar outro bem cria outro pagamento de dação. A razão é de rastreabilidade — com
 * vários bens num lançamento só, o pagamento tinha um total que não voltava para as peças, e
 * identificar qual bem quitou qual título virava adivinhação. Um bem por pagamento dá a cada um o
 * seu valor, a sua data e a sua identificação.
 *
 * Por isso também não há mais estado de "expandido": não existe lista para colapsar, e os campos do
 * bem aparecem assim que o tipo de pagamento é escolhido.
 *
 * Os campos condicionais por `asset_type` continuam como estavam.
 */
import { AssetType, assetTypeLabels, AssetDationItem } from '../../../constants/payment';

interface Props {
    value: AssetDationItem;
    onChange: (item: AssetDationItem) => void;
}

export const emptyAsset = (type: AssetType = AssetType.VEHICLE): AssetDationItem => ({
    asset_type: type,
    description: '',
    estimated_value: 0,
});

const formatCurrency = (amount: number) =>
    amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const AssetDationForm: React.FC<Props> = ({ value, onChange }) => {
    const update = (field: string, fieldValue: any) =>
        onChange({ ...value, [field]: fieldValue });

    return (
        <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Bem dado em pagamento
                {value.estimated_value > 0 && (
                    <span className="ml-2 text-green-600 font-normal">
                        {formatCurrency(value.estimated_value)}
                    </span>
                )}
            </label>

            <div className="border dark:border-slate-600 rounded p-3 bg-gray-50 dark:bg-slate-700/50">
                        <div className="space-y-2">
                            {/* Type selector */}
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Tipo de bem</label>
                                    <select className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                        value={value.asset_type} onChange={e => update('asset_type', e.target.value)}>
                                        {Object.entries(assetTypeLabels).map(([k, v]) => (
                                            <option key={k} value={k}>{v}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Valor estimado (R$)</label>
                                    <input type="number" step="0.01" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                        value={value.estimated_value || ''} onChange={e => update('estimated_value', parseFloat(e.target.value) || 0)} />
                                </div>
                            </div>

                            {/* Description (all types) */}
                            <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Descrição</label>
                                <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                    value={value.description} onChange={e => update('description', e.target.value)}
                                    placeholder="Ex: Plantadeira Planticenter 2020" />
                            </div>

                            {/* VEHICLE fields */}
                            {value.asset_type === AssetType.VEHICLE && (
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Placa</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.plate || ''} onChange={e => update('plate', e.target.value)} placeholder="ABC-1234" />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Chassi</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.chassis || ''} onChange={e => update('chassis', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Renavam</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.renavam || ''} onChange={e => update('renavam', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Marca</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.brand || ''} onChange={e => update('brand', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Modelo</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.model || ''} onChange={e => update('model', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Ano</label>
                                        <input type="number" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.year || ''} onChange={e => update('year', parseInt(e.target.value) || undefined)} />
                                    </div>
                                </div>
                            )}

                            {/* MACHINERY fields */}
                            {value.asset_type === AssetType.MACHINERY && (
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Marca</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.brand || ''} onChange={e => update('brand', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Modelo</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.model || ''} onChange={e => update('model', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Nº Série</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.serial_number || ''} onChange={e => update('serial_number', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Ano</label>
                                        <input type="number" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.year || ''} onChange={e => update('year', parseInt(e.target.value) || undefined)} />
                                    </div>
                                </div>
                            )}

                            {/* REAL_ESTATE fields */}
                            {value.asset_type === AssetType.REAL_ESTATE && (
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Matrícula</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.registration_number || ''} onChange={e => update('registration_number', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Cartório</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.registry_office || ''} onChange={e => update('registry_office', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Área total (ha)</label>
                                        <input type="number" step="0.01" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.total_area || ''} onChange={e => update('total_area', parseFloat(e.target.value) || undefined)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Área edificada (m²)</label>
                                        <input type="number" step="0.01" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.built_area || ''} onChange={e => update('built_area', parseFloat(e.target.value) || undefined)} />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Tipo</label>
                                        <select className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.land_type || 'RURAL'} onChange={e => update('land_type', e.target.value)}>
                                            <option value="RURAL">Rural</option>
                                            <option value="URBAN">Urbano</option>
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* PRODUCT / GRAIN fields */}
                            {(value.asset_type === AssetType.PRODUCT || value.asset_type === AssetType.GRAIN) && (
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Tipo de produto</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.product_type || ''} onChange={e => update('product_type', e.target.value)}
                                            placeholder="Soja, Milho, Algodão..." />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Quantidade</label>
                                        <input type="number" step="0.01" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.quantity || ''} onChange={e => update('quantity', parseFloat(e.target.value) || undefined)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Unidade</label>
                                        <select className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.unit || 'TONELADAS'} onChange={e => update('unit', e.target.value)}>
                                            <option value="TONELADAS">Toneladas</option>
                                            <option value="SACAS">Sacas</option>
                                            <option value="KG">Kg</option>
                                            <option value="LITROS">Litros</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Local de armazenamento</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={value.storage_location || ''} onChange={e => update('storage_location', e.target.value)} />
                                    </div>
                                </div>
                            )}
                        </div>
            </div>

            {/* Future: search existing collateral assets */}
            {/* <button type="button" className="w-full text-xs text-blue-500 hover:text-blue-700 mt-2">
                🔍 Buscar assets no módulo de garantias (em breve)
            </button> */}
        </div>
    );
};

export default AssetDationForm;
